import Loader from '@anm/components/Loader';
import { useColumn } from '@anm/components/list/Masonry';
import BrowseMoreButton from '@anm/components/stocks/BrowseMoreButton';
import Wrapper, { ListWrapper, MainTitle } from '@anm/components/stocks/StockListNew';
import isMobile from '@anm/helpers/is/isMobile';
import { toUrlParams } from '@anm/helpers/parseParams';
import usePageParams from '@anm/hooks/usePageParams';
import useResize from '@anm/hooks/useResize';
import useSignupRedirect from '@anm/hooks/useSignupRedirect';
import useUpdateOnMount from '@anm/hooks/useUpdateOnMount';
import { memo, FC } from 'react';
import { useSelector } from 'react-redux';

import vtConfig from '../../../config';
import { authSelectors } from '../../../store/modules/auth';
import { stockSelectors } from '../../../store/modules/stocks';

import StockItem from './StockItem';
import { getStockList } from './helpers';

export const LIST_LIMIT = 15;
export const TEMPLATE_STOCKS_PARAMS = {
  page: 1,
  limit: 30,
  sort: 'POPULAR'
};

const { urls } = vtConfig();

type StockListProps = {
  title: string;
};

const StockList: FC<StockListProps> = ({ title }) => {
  const listKey = useUpdateOnMount();
  const { columnCount, columnWidth, reCalculateColumn } = useColumn({
    1959: 5,
    1639: 4,
    1319: 2,
    800: 2,
    767: 3,
    default: isMobile() ? 1 : 5
  });
  useResize(reCalculateColumn);

  const isUserLogged = useSelector(authSelectors.isUserLogged);
  const isStocksPending = useSelector(stockSelectors.selectStockIsPending);
  const stockList = useSelector(stockSelectors.selectStockList);
  const stockParams = useSelector(stockSelectors.selectStocksParams);

  const { embed, folderId } = usePageParams();
  const list = getStockList(stockList, columnCount);

  const searchQuery = stockParams?.searchName;

  const params = {
    new: 1,
    s: searchQuery,
    target: 'stock-library',
    ...(embed && { embed }),
    ...(folderId && { folderId })
  };
  const urlParams = toUrlParams(params);
  const editorAllStocksUrl = `${urls.editor}/#${urlParams}`;

  const allStocksButtonUrl = useSignupRedirect({
    isUserLogged,
    domain: urls.wave,
    link: editorAllStocksUrl
  });

  if (isStocksPending) return <Loader size="large" color="blue" />;

  if (!list.length) return null;

  const mainTitle = `Start with “${title || searchQuery}” stock videos and images:`;

  return (
    <Wrapper {...{ columnWidth, columnCount }} key={mainTitle}>
      <MainTitle level={2} title={mainTitle} />
      <ListWrapper key={listKey}>
        {list.map((stock, index) => (
          <StockItem key={stock.id + stock.service} {...{ stock, index, columnCount, columnWidth }} />
        ))}
        <BrowseMoreButton width={columnWidth} href={allStocksButtonUrl} columnCount={columnCount} />
      </ListWrapper>
    </Wrapper>
  );
};

export default memo(StockList);
